/**
 * viewer-settings.ts — per-scene viewer behavior authored in the editor.
 *
 * Holds the navigation mode (walk vs orbit) and the orbit-camera tuning
 * (pitch floor, zoom band, idle auto-rotate) that ship to the viewer via
 * scene.json (`navigation` + `camera.orbit`). Persisted in the .ssproj /
 * editor-meta.json sidecar via buildDocMeta's `viewer` block, so it
 * round-trips like markers/walk do.
 *
 * Events:
 *  - invoke 'viewer.settings'          → snapshot (plain JSON)
 *  - fire   'viewer.setSettings', obj  → shallow-merge + change event
 *  - fire   'viewer.deserialize', obj  → replace from a loaded document
 *  - on     'viewer.settingsChanged'   → UI refresh hook
 */

import type { Events } from '../events';
import type { NavigationMode, OrbitConfig } from '../../../shared/scene-core/scene-schema';

export interface ViewerSettings {
    navigation: NavigationMode;
    orbit: OrbitConfig;
}

// Defaults mirror the viewer's built-in polish for rotating scenes so the UI
// shows the effective values even before the user touches anything.
const defaults = (): ViewerSettings => ({
    navigation: 'free',
    orbit: {
        pitchMinDeg: 16,
        // Zoom limits in WORLD METERS, authored via capture-from-view.
        // 0 = not set → the viewer applies its radius-based defaults.
        zoomMinM: 0,
        zoomMaxM: 0,
        autoRotate: true,
        autoRotateSpeed: 3,
        autoRotateDelay: 5
    }
});

const registerViewerSettings = (events: Events) => {
    let settings = defaults();

    events.function('viewer.settings', (): ViewerSettings => ({
        navigation: settings.navigation,
        orbit: { ...settings.orbit }
    }));

    events.on('viewer.setSettings', (patch: Partial<ViewerSettings>) => {
        if (!patch) return;
        if (patch.navigation) settings.navigation = patch.navigation;
        if (patch.orbit) settings.orbit = { ...settings.orbit, ...patch.orbit };
        events.fire('viewer.settingsChanged');
    });

    events.on('viewer.deserialize', (data: Partial<ViewerSettings> | null) => {
        settings = defaults();
        if (data?.navigation) settings.navigation = data.navigation;
        if (data?.orbit) settings.orbit = { ...settings.orbit, ...data.orbit };
        events.fire('viewer.settingsChanged');
    });

    // New/reset document → back to defaults.
    events.on('doc.setName', (name: string | null) => {
        if (name === null) {
            settings = defaults();
            events.fire('viewer.settingsChanged');
        }
    });
};

export { registerViewerSettings };
